import { stripInlineMarkdown } from './textFormatting.js';

const CATEGORY_ORDER = [
  'raumaufteilung',
  'raumfluss',
  'lichteinfall',
  'funktionalitaet',
  'stauraum',
  'privatsphaere',
  'zugaenglichkeit',
  'flexibilitaet',
  'aussenbezug',
];

const RATING_TO_SCORE = {
  positive: 5,
  positiv: 5,
  good: 4,
  gut: 4,
  average: 3,
  neutral: 3,
  fair: 3,
  poor: 2,
  negative: 2,
  negativ: 2,
  bad: 2,
};

const ARCHETYPES = [
  'add_room',
  'merge_rooms',
  'open_wall',
  'relocate_entrance',
  'redesign_kitchen',
  '3d_vignette',
];

// Material Design Icons keyed per archetype. The Studio suggestion card
// renders a tinted tile with this icon instead of a static blueprint SVG —
// the SVGs were misleading (looked AI-generated but were hand-authored
// placeholders) and they got distorted by aspect-ratio mismatch.
const ARCHETYPE_ICON = {
  add_room: 'mdi-plus-box-multiple-outline',
  merge_rooms: 'mdi-vector-combine',
  open_wall: 'mdi-wall',
  relocate_entrance: 'mdi-door-open',
  redesign_kitchen: 'mdi-stove',
  '3d_vignette': 'mdi-cube-outline',
};

const FALLBACK_ARCHETYPES = ['open_wall', 'add_room', '3d_vignette'];

function compact(value) {
  return typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';
}

function plain(value) {
  return stripInlineMarkdown(compact(value));
}

function clip(text, max) {
  if (!text || text.length <= max) return text;
  const slice = text.slice(0, max);
  const cut = slice.lastIndexOf(' ');
  return `${(cut > max * 0.6 ? slice.slice(0, cut) : slice).trim()}…`;
}

function splitSentences(text) {
  return compact(text).match(/[^.!?]+[.!?]+|[^.!?]+$/g)?.map((sentence) => sentence.trim()) || [];
}

function twoSentenceSummary(primary, fallback, fallbackText) {
  const source = plain(primary) || plain(fallback);
  if (!source) return fallbackText;
  const sentences = splitSentences(source).slice(0, 2);
  return sentences.join(' ') || fallbackText;
}

function getCategoryScore(category) {
  if (!category || typeof category !== 'object') return null;

  const scores = Object.entries(category)
    .filter(([key, value]) => key !== 'description' && value && typeof value === 'object')
    .map(([, value]) => {
      if (typeof value.score === 'number') return value.score;
      if (typeof value.rating === 'number') return value.rating;
      return RATING_TO_SCORE[String(value.rating || '').toLowerCase()] || null;
    })
    .filter((value) => Number.isFinite(value));

  if (scores.length === 0) return null;
  return Math.round((scores.reduce((sum, score) => sum + score, 0) / scores.length) * 10) / 10;
}

function getScoredCategories(analysis, t) {
  const safeAnalysis = analysis && typeof analysis === 'object' ? analysis : {};
  return CATEGORY_ORDER
    .map((key) => ({
      key,
      label: t(`analysis.results.categories.${key}`),
      score: getCategoryScore(safeAnalysis[key]),
    }))
    .filter((category) => Number.isFinite(category.score));
}

function archetypeForText(text) {
  const normalized = compact(text).toLowerCase();
  if (/3d|render|visual|vignette|perspective/.test(normalized)) return '3d_vignette';
  if (/merge|combine|suite|zusammen|verbinden/.test(normalized)) return 'merge_rooms';
  if (/open|wall|wand|durchbruch/.test(normalized)) return 'open_wall';
  if (/kitchen|küche|pantry|speisekammer/.test(normalized)) return 'redesign_kitchen';
  if (/entrance|entry|eingang/.test(normalized)) return 'relocate_entrance';
  if (/add|room|zimmer|storage|stauraum/.test(normalized)) return 'add_room';
  return 'open_wall';
}

function buildSuggestion(source, index, t) {
  const archetypeFallback = FALLBACK_ARCHETYPES[index] || 'open_wall';
  const archetype = ARCHETYPES.includes(source.archetype)
    ? source.archetype
    : archetypeForText(`${source.title || ''} ${source.description || ''}`) || archetypeFallback;
  const titleText = plain(source.title) || plain(source.description) || t(`analysis.studio.fallbackSuggestion.${archetypeFallback}.title`);
  const descriptionRaw = plain(source.description) || plain(source.title) || t(`analysis.studio.fallbackSuggestion.${archetypeFallback}.description`);

  return {
    id: `studio-suggestion-${index + 1}`,
    // Title is kept short for the card heading. Description is NOT clipped —
    // it becomes part of the directive sent to the image-gen model
    // (`${title}. ${description}`); a truncated string with a literal `…`
    // would silently corrupt the prompt. The card uses CSS line-clamp for
    // visual rhythm and a tooltip exposes the full text.
    title: clip(titleText, 90),
    description: descriptionRaw,
    archetype,
    archetypeLabel: t(`analysis.studio.archetype.${archetype}`),
    archetypeIcon: ARCHETYPE_ICON[archetype] || ARCHETYPE_ICON.open_wall,
    // NOTE: a suggestion has no per-card price. Every studio step costs the
    // same configurable `studio_step_image` rate, so the card, the CTA and
    // the submitted expectedCost all read `hub.studioStepCredits` from the
    // resolved pricing rules. A hardcoded `creditCost` here used to render a
    // stale "1" on the card while the action charged the configured amount.
  };
}

// Wraps raw `{ archetype, title, description }` items coming straight from
// the backend `/api/studio/starter-pack` response with the same metadata
// the local builder adds (id, archetypeLabel, archetypeIcon, creditCost),
// so the Studio card grid can render either source identically.
export function buildSuggestionsFromStarterPack(rawSuggestions, t) {
  if (typeof t !== 'function') {
    throw new Error('buildSuggestionsFromStarterPack requires a translator function `t`.');
  }
  if (!Array.isArray(rawSuggestions)) return [];
  return rawSuggestions.map((source, index) => buildSuggestion({
    title: source?.title || '',
    description: source?.description || '',
    archetype: source?.archetype,
  }, index, t));
}

export function buildStudioSuggestions({ analysis = {}, userIntent = {}, t } = {}) {
  if (typeof t !== 'function') {
    throw new Error('buildStudioSuggestions requires a translator function `t`.');
  }

  const safeAnalysis = analysis && typeof analysis === 'object' ? analysis : {};
  const safeIntent = userIntent || {};
  const intentText = compact(safeIntent.expectations).toLowerCase();
  const seeded = [
    ...((Array.isArray(safeAnalysis.suggestions) ? safeAnalysis.suggestions : []).slice(0, 4)),
  ];

  if (/merge|combine|suite|zusammen|verbinden/.test(intentText)) {
    seeded.push({
      title: t('analysis.studio.intentSuggestion.merge.title'),
      description: t('analysis.studio.intentSuggestion.merge.description', {
        intent: compact(safeIntent.expectations),
      }),
      archetype: 'merge_rooms',
    });
  }

  if (/kitchen|küche|pantry|speisekammer/.test(intentText)) {
    seeded.push({
      title: t('analysis.studio.intentSuggestion.kitchen.title'),
      description: t('analysis.studio.intentSuggestion.kitchen.description', {
        intent: compact(safeIntent.expectations),
      }),
      archetype: 'redesign_kitchen',
    });
  }

  const sources = seeded.length > 0
    ? seeded
    : FALLBACK_ARCHETYPES.map((archetype) => ({
      title: t(`analysis.studio.fallbackSuggestion.${archetype}.title`),
      description: t(`analysis.studio.fallbackSuggestion.${archetype}.description`),
      archetype,
    }));

  return sources
    .map((source, index) => buildSuggestion(source, index, t))
    .filter((suggestion, index, all) =>
      all.findIndex((candidate) => candidate.title === suggestion.title) === index,
    )
    .slice(0, 6);
}

export function buildHubViewModel({
  analysis = {},
  costAnalysis = null,
  roomAnalysis = null,
  evaluation = '',
  userIntent = {},
  credits = 0,
  pricingRules = null,
  t,
} = {}) {
  if (typeof t !== 'function') {
    throw new Error('buildHubViewModel requires a translator function `t`.');
  }

  const safeAnalysis = analysis && typeof analysis === 'object' ? analysis : {};
  const safeIntent = userIntent || {};
  const scored = getScoredCategories(safeAnalysis, t);
  const strongest = scored.length > 0 ? [...scored].sort((a, b) => b.score - a.score)[0] : null;
  const weakest = scored.length > 0 ? [...scored].sort((a, b) => a.score - b.score)[0] : null;
  const roomCount = Array.isArray(roomAnalysis?.results) ? roomAnalysis.results.length : 0;
  const suggestions = Array.isArray(safeAnalysis.suggestions) ? safeAnalysis.suggestions : [];
  const costLevel = compact(costAnalysis?.costRangeQualitative?.level);
  const costScore = Number.isFinite(costAnalysis?.overallScore) ? costAnalysis.overallScore : null;
  const topMove = plain(suggestions[0]?.title);

  const analysisTeasers = [
    strongest && t('analysis.productHub.analysis.teaser.strongest', {
      label: strongest.label,
      score: strongest.score,
    }),
    weakest && t('analysis.productHub.analysis.teaser.weakest', {
      label: weakest.label,
      score: weakest.score,
    }),
    costLevel && (costScore
      ? t('analysis.productHub.analysis.teaser.costWithScore', { level: costLevel, score: costScore })
      : t('analysis.productHub.analysis.teaser.cost', { level: costLevel })),
    roomCount && t('analysis.productHub.analysis.teaser.roomCount', { count: roomCount }),
    topMove && t('analysis.productHub.analysis.teaser.topMove', { title: clip(topMove, 90) }),
  ].filter(Boolean).slice(0, 5);

  // Pricing rules: paid tier costs, with safe fallbacks so the Hub never
  // shows "undefined credits" while the rules are still loading.
  const paidRules = pricingRules || {};
  const analysisCostCredits = Number.isFinite(paidRules.upload) ? paidRules.upload : 1;
  const studioEntryCredits = Number.isFinite(paidRules.open_studio) ? paidRules.open_studio : 0;
  const studioStepCredits = Number.isFinite(paidRules.studio_step_image) ? paidRules.studio_step_image : 1;

  return {
    question: plain(safeIntent.expectations) || t('analysis.productHub.questionFallback'),
    requirementsAnswer: twoSentenceSummary(
      evaluation,
      '',
      t('analysis.productHub.requirementsFallback'),
    ),
    credits: Number.isFinite(credits) ? credits : 0,
    analysisTeasers,
    studioPrompts: buildStudioSuggestions({ analysis: safeAnalysis, userIntent: safeIntent, t }).slice(0, 4),
    analysisCostCredits,
    studioEntryCredits,
    studioStepCredits,
  };
}

export function getAnalysisProductMode(route = {}) {
  if (route.name === 'AnalysisHub') return 'hub';
  if (route.name === 'Analysis360') return '360';
  if (route.name === 'AnalysisStudio') return 'studio';
  return null;
}
